import React, { Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { AppShell } from "./components/AppShell";
import { useAuth } from "./components/AuthProvider";
import { RequireAssessment } from "./components/RequireAssessment";
import { UnsavedNavigationGuardProvider } from "./contexts/UnsavedNavigationGuardContext";
import { useRuntimeConfig } from "./contexts/RuntimeConfigContext";

function lazyNamed<T extends React.ComponentType<any>>(
  loader: () => Promise<Record<string, T>>,
  exportName: string
) {
  return React.lazy(async () => {
    const module = await loader();
    return { default: module[exportName] };
  });
}

const LoginPage = lazyNamed(() => import("./pages/LoginPage"), "LoginPage");
const ForcedPasswordChangePage = lazyNamed(
  () => import("./pages/ForcedPasswordChangePage"),
  "ForcedPasswordChangePage"
);
const AuthCallbackPage = lazyNamed(() => import("./pages/AuthCallbackPage"), "AuthCallbackPage");
const AssessmentsPage = lazyNamed(() => import("./pages/AssessmentsPage"), "AssessmentsPage");
const OverviewPage = lazyNamed(() => import("./pages/OverviewPage"), "OverviewPage");
const TaskListPage = lazyNamed(() => import("./pages/TaskListPage"), "TaskListPage");
const ObjectsPage = lazyNamed(() => import("./pages/ObjectsPage"), "ObjectsPage");
const RisksPage = lazyNamed(() => import("./pages/RisksPage"), "RisksPage");
const MitigationsPage = lazyNamed(() => import("./pages/MitigationsPage"), "MitigationsPage");
const ReportsPage = lazyNamed(() => import("./pages/ReportsPage"), "ReportsPage");
const ThreatModelPage = lazyNamed(() => import("./pages/ThreatModelPage"), "ThreatModelPage");
const AdminProductTypesPage = lazyNamed(() => import("./pages/AdminProductTypesPage"), "AdminProductTypesPage");
const AdminSecurityLevelsPage = lazyNamed(
  () => import("./pages/AdminSecurityLevelsPage"),
  "AdminSecurityLevelsPage"
);
const AdminScalePairsPage = lazyNamed(() => import("./pages/AdminScalePairsPage"), "AdminScalePairsPage");
const AdminUsersPage = lazyNamed(() => import("./pages/AdminUsersPage"), "AdminUsersPage");
const ProfilePage = lazyNamed(() => import("./pages/ProfilePage"), "ProfilePage");

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { tokens, isReady } = useAuth();
  if (!tokens) return <Navigate to="/login" replace />;
  if (!isReady) return null;
  return <>{children}</>;
}

function RequireFeature({
  feature,
  redirectTo = "/assessments",
  children,
}: {
  feature: string;
  redirectTo?: string;
  children: React.ReactNode;
}) {
  const { features } = useRuntimeConfig();
  if (features[feature] === false) return <Navigate to={redirectTo} replace />;
  return <>{children}</>;
}

export function App() {
  return (
    <Suspense fallback={null}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/change-temporary-password" element={<ForcedPasswordChangePage />} />
        <Route path="/auth/callback" element={<AuthCallbackPage />} />
        <Route
          path="/"
          element={
            <RequireAuth>
              <UnsavedNavigationGuardProvider>
                <AppShell />
              </UnsavedNavigationGuardProvider>
            </RequireAuth>
          }
        >
          <Route index element={<Navigate to="/assessments" replace />} />
          <Route path="assessments" element={<AssessmentsPage />} />
          <Route path="workspace/overview" element={<RequireAssessment><OverviewPage /></RequireAssessment>} />
          <Route
            path="workspace/task-list"
            element={
              <RequireFeature feature="task_list" redirectTo="/workspace/overview">
                <RequireAssessment>
                  <TaskListPage />
                </RequireAssessment>
              </RequireFeature>
            }
          />
          <Route path="workspace/objects" element={<RequireAssessment><ObjectsPage /></RequireAssessment>} />
          <Route path="workspace/risks" element={<RequireAssessment><RisksPage /></RequireAssessment>} />
          <Route path="workspace/mitigations" element={<RequireAssessment><MitigationsPage /></RequireAssessment>} />
          <Route path="workspace/reports" element={<RequireAssessment><ReportsPage /></RequireAssessment>} />
          <Route
            path="workspace/threat-model"
            element={
              <RequireFeature feature="threat_modeling">
                <RequireAssessment>
                  <ThreatModelPage />
                </RequireAssessment>
              </RequireFeature>
            }
          />
          <Route path="admin/product-types" element={<AdminProductTypesPage />} />
          <Route path="admin/security-levels" element={<AdminSecurityLevelsPage />} />
          <Route path="admin/scale-pairs" element={<AdminScalePairsPage />} />
          <Route path="profile" element={<ProfilePage />} />
          <Route path="admin/users" element={<AdminUsersPage />} />
          <Route path="*" element={<Navigate to="/assessments" replace />} />
        </Route>
      </Routes>
    </Suspense>
  );
}
